import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Avatar, Icon, ToastProvider, UnsavedGuardProvider } from './components/common';
import { AccountModalProvider } from './components/account/AccountModalProvider';
import { CreateModalProvider } from './components/CreateModalProvider';
import { TopBar } from './components/shell/TopBar';
import { DatePicker } from './components/shell/DatePicker';
import { datePickerMode } from './components/shell/datePickerMode';
import { NewMenu, NotificationBell } from './components/shell/TopBarActions';
import { GlobalSearch } from './components/search/GlobalSearch';
import { RecordModalProvider } from './components/search/RecordModalProvider';
import { DeskNotificationsProvider } from './components/notifications/DeskNotifications';
import { PageHost } from './pages';
import { pathToView, viewToPath, visibleNav, type Role } from './pages/routeMap';
import { deskLanding } from './customer/landing';
import { NavProvider } from './pages/NavContext';
import { signOutSession } from './lib/signOut';
import { recordPageView } from './lib/activity';
import { startOfDay } from './lib/dates';

// App shell — the prototype sidebar (NAV in the route map) + a main area that
// renders the active section through the keep-alive page host. AppShell owns the
// sidebar/nav and the active-key state; page bodies live under src/pages/ (AC3),
// and the PageHost keeps visited pages mounted across nav (keep-alive-page-host
// AC1/AC2). Switching `view` toggles which mounted page is visible, not which is
// mounted.
export function AppShell({
  me,
}: {
  me: { name: string; email: string; role: Role };
}) {
  const navigate = useNavigate();
  const loc = useLocation();
  // The active section is DERIVED from the URL (not local state), so a page is
  // bookmarkable and the back/forward buttons work. PageHost still keeps every
  // visited page mounted — the URL only decides which is visible.
  const view = pathToView(loc.pathname);
  // THE URL ALWAYS NAMES THE PAGE. The bare root, a customer URL opened at the
  // desk, a typo — all land on the default section, by the one rule in
  // `customer/landing.ts` rather than a special case for `/` here. Freddi,
  // 2026-09-14: a signed-in account whose role names a destination is SENT
  // there; it is never shown a page offering to send it.
  useEffect(() => {
    const to = deskLanding(loc.pathname);
    if (to) navigate(to, { replace: true });
  }, [loc.pathname, navigate]);

  // Page-view metrics (admin): record the dwell on a section when it changes,
  // and flush the open one when the tab is hidden/closed. Active time between
  // navigations — approximate (idle/backgrounded time isn't subtracted).
  const pageRef = useRef({ view, at: Date.now() });
  useEffect(() => {
    const prev = pageRef.current;
    if (prev.view !== view) {
      recordPageView(prev.view, Date.now() - prev.at);
      pageRef.current = { view, at: Date.now() };
    }
  }, [view]);
  useEffect(() => {
    const flush = () => {
      recordPageView(pageRef.current.view, Date.now() - pageRef.current.at);
      pageRef.current = { view: pageRef.current.view, at: Date.now() };
    };
    window.addEventListener('pagehide', flush);
    return () => window.removeEventListener('pagehide', flush);
  }, []);
  const [bookingIntent, setBookingIntent] = useState<string | null>(null);
  const [statusIntent, setStatusIntent] = useState<string | null>(null);
  const [dateIntent, setDateIntent] = useState<{ view: string; date: string } | null>(null);
  const [windowIntent, setWindowIntent] = useState<{ view: string; window: { from: string; to: string } } | null>(null);
  const go = (v: string, opts?: { bookingId?: string; status?: string; date?: string; window?: { from: string; to: string } }) => {
    navigate(viewToPath(v));
    if (opts?.bookingId) setBookingIntent(opts.bookingId);
    if (opts?.status) setStatusIntent(opts.status);
    if (opts?.date) setDateIntent({ view: v, date: opts.date });
    if (opts?.window) setWindowIntent({ view: v, window: opts.window });
  };

  // User menu (sign out). Clicking the avatar toggles the menu; signing out
  // clears the session then does a full load to '/', which re-probes /me →
  // signed out → the sign-in screen.
  const [menuOpen, setMenuOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  // The one shared anchor date behind the top-bar picker (AC4). Owned here so a
  // single date threads through every page; each reads it at its own granularity.
  const [anchor, setAnchor] = useState<Date>(() => startOfDay(new Date()));
  // Scroll-shrink: past the top, the bar collapses its tagline + height on one
  // easing (AC1). The New button deliberately does NOT change — that's in the bar
  // markup, not here.
  const [shrunk, setShrunk] = useState(false);
  useEffect(() => {
    const onScroll = () => setShrunk(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const signOut = () => void signOutSession(setSigningOut);
  const roleLabel = me.role ? me.role[0]!.toUpperCase() + me.role.slice(1) : 'Staff';

  // One source for both navs (sidebar + mobile bottom bar). The Operations group
  // is the bottom bar's tabs; everything else (Business, Admin) lives behind the
  // "More" tab so the bar stays at five thumb-sized cells.
  const navGroups = visibleNav(me.role);
  const bottomItems = navGroups.find((g) => g.group === 'Operations')?.items ?? [];
  const moreGroups = navGroups.filter((g) => g.group !== 'Operations');

  return (
    <ToastProvider>
    {/* THE GUARD OUTSIDE THE ACCOUNT MODAL'S PROVIDER. That provider renders
        its modal host as a sibling AFTER its children, so a guard nested
        inside the children was invisible to the access editor, and an edited
        email or role closed without a word (review on #239). */}
    <UnsavedGuardProvider>
    <AccountModalProvider myEmail={me.email.toLowerCase()}>
    <CreateModalProvider>
    <RecordModalProvider>
    <DeskNotificationsProvider>
    {/* THE RAIL IS PERMANENT (docs/decision/sidebar-always-collapsed.md). The
        class is unconditional rather than stateful: there is no control that
        could change it, so there is no state for one to change. The persisted
        preference was removed rather than pinned to `true` — a value nothing
        reads cannot be honoured by accident, and a machine that stored
        "expanded" therefore shows the rail with nothing to clear. */}
    <div className={'app sidebar-layout sidebar-collapsed' + (shrunk ? ' tb-shrunk' : '')}>
      {/* The global top bar owns the brand landmark, the one global search, the
          contextual date picker, and the teal New menu + notification bell.
          Mounted on every page — it replaces the per-page headers (AC1). */}
      <TopBar
        shrunk={shrunk}
        search={<GlobalSearch canSeeUsers={me.role === 'admin'} />}
        center={
          <DatePicker
            mode={datePickerMode(view)}
            value={anchor}
            onChange={setAnchor}
            today={startOfDay(new Date())}
          />
        }
        actions={
          <>
            <NewMenu isAdmin={me.role === 'admin'} />
            <NotificationBell />
          </>
        }
      />

      <div className="shell-body">
      <aside className="sidebar">
        <nav className="sidebar-nav">
          {/* visibleNav drops admin-only items for non-admins AND any group
              left empty (heading included) — so the whole "Admin" section
              disappears, not just the link. */}
          {navGroups.map((group) => {
            return (
              <div key={group.group} className="sb-group">
                <div className="sb-group-label">{group.group}</div>
                {group.items.map((it) => (
                  <button
                    key={it.key}
                    className={'sb-item' + (view === it.key ? ' active' : '')}
                    // THE ONLY AFFORDANCE LEFT. The rail is icons permanently,
                    // so the page name lives here or nowhere at all — and it
                    // needs BOTH attributes, which is the app's own convention
                    // for an icon-only control (see Avatar.tsx).
                    // `title` alone is a sighted-hover affordance and a WEAK
                    // accessible name: the `<span>` carrying the label is
                    // display:none under the permanent rail and so out of the
                    // accessibility tree, the glyph is aria-hidden, and a touch
                    // screen reader never fires the hover that would reveal a
                    // title. Before this story that fell only on users who chose
                    // to collapse the sidebar; it now falls on every one of them,
                    // on every page, with no way back.
                    title={it.label}
                    aria-label={it.label}
                    onClick={() => navigate(it.path)}
                  >
                    <Icon name={it.icon} size={16} />
                    <span>{it.label}</span>
                  </button>
                ))}
              </div>
            );
          })}
        </nav>

        <div className="sidebar-foot">
          {menuOpen && <div className="usermenu-backdrop" onClick={() => setMenuOpen(false)} />}
          <div className="sidebar-userwrap">
            {menuOpen && (
              <div className="popover sidebar-usermenu" role="menu">
                <button className="usermenu-item" role="menuitem" onClick={signOut} disabled={signingOut}>
                  <Icon name="log-out" size={15} />
                  <span>{signingOut ? 'Signing out…' : 'Sign out'}</span>
                </button>
              </div>
            )}
            <button
              className="btn ghost sm sidebar-user"
              onClick={() => setMenuOpen((o) => !o)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              // SAME PROBLEM, ONE CONTROL FURTHER DOWN. `.sidebar-user-id` and
              // the chevron are hidden under the permanent rail, so this button
              // was left showing initials alone and announcing them as its name
              // — with nothing to say that it opens the menu holding Sign out.
              title={`${me.name} — account and sign out`}
              aria-label={`${me.name} — account and sign out`}
            >
              <Avatar name={me.name} id={me.email} size={26} />
              <div className="sidebar-user-id">
                <div className="sidebar-user-name">{me.name}</div>
                <div className="sidebar-user-role">{roleLabel}</div>
              </div>
              <Icon name="chevron-down" size={14} />
            </button>
          </div>
        </div>
      </aside>

      <main className="main">
        <NavProvider
          value={{
            go,
            bookingIntent,
            consumeBookingIntent: () => setBookingIntent(null),
            statusIntent,
            consumeStatusIntent: () => setStatusIntent(null),
            dateIntent,
            consumeDateIntent: () => setDateIntent(null),
          windowIntent,
          consumeWindowIntent: () => setWindowIntent(null),
            anchor,
            setAnchor,
            role: me.role,
          }}
        >
          <PageHost view={view} />
        </NavProvider>
      </main>
      </div>

      {/* Mobile bottom bar (≤754px, CSS-gated). The four Operations tabs + a
          "More" cell opening a sheet with the rest (Business, Admin) + sign out.
          Same visibleNav source as the sidebar — no duplicated nav list. */}
      <nav className="bottom-bar" aria-label="Primary">
        {bottomItems.map((it) => (
          <button
            key={it.key}
            className={'bb-item' + (view === it.key ? ' active' : '')}
            onClick={() => {
              navigate(it.path);
              setMoreOpen(false);
            }}
          >
            <Icon name={it.icon} size={20} />
            <span>{it.label.split(' ')[0]}</span>
          </button>
        ))}
        <button
          className={'bb-item' + (moreOpen ? ' active' : '')}
          onClick={() => setMoreOpen((o) => !o)}
          aria-haspopup="menu"
          aria-expanded={moreOpen}
        >
          <Icon name="menu" size={20} />
          <span>More</span>
        </button>
      </nav>

      {moreOpen && (
        <div className="more-backdrop" onClick={() => setMoreOpen(false)}>
          <div className="more-sheet" role="menu" onClick={(e) => e.stopPropagation()}>
            <div className="more-sheet-grip" />
            {moreGroups.map((group) => (
              <div key={group.group} className="more-group">
                <div className="more-group-label">{group.group}</div>
                {group.items.map((it) => (
                  <button
                    key={it.key}
                    className={'more-item' + (view === it.key ? ' active' : '')}
                    role="menuitem"
                    onClick={() => {
                      navigate(it.path);
                      setMoreOpen(false);
                    }}
                  >
                    <Icon name={it.icon} size={18} />
                    <span>{it.label}</span>
                  </button>
                ))}
              </div>
            ))}
            <div className="more-group">
              <div className="more-user">
                <Avatar name={me.name} id={me.email} size={28} />
                <div className="more-user-id">
                  <div className="more-user-name">{me.name}</div>
                  <div className="more-user-role">{roleLabel}</div>
                </div>
              </div>
              <button className="more-item" role="menuitem" onClick={signOut} disabled={signingOut}>
                <Icon name="log-out" size={18} />
                <span>{signingOut ? 'Signing out…' : 'Sign out'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </DeskNotificationsProvider>
    </RecordModalProvider>
    </CreateModalProvider>
    </AccountModalProvider>
    </UnsavedGuardProvider>
    </ToastProvider>
  );
}
